/**
 * The timesheet — "for record and payroll".
 *
 * Totals first, because the number per person over a period is what a pay run
 * needs; the individual shifts underneath are what you check when somebody
 * disagrees with it. An owner or a manager sees everybody and can correct a
 * shift; anybody else sees their own hours and nothing else.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Pencil, Printer, Trash2 } from "lucide-react";
import { Button, Input, Label, Modal, ModalFooter, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { hoursLabel, type Shift } from "./TimeClock";

interface Total {
  user_id: string;
  staff_name: string | null;
  hours: number;
  shifts: number;
  /** Dates worked. Two shifts in one day is one day on a pay run. */
  days: number;
  /** How many of their shifts carry an explanation. */
  notes: number;
  /** Shifts still running — nobody should be paid off a clock that is on. */
  open: number;
}

/** "Late · no internet" — what a shift's explanation reads as on the report. */
function noteLine(s: Shift): string | null {
  const bits: string[] = [];
  const add = (label: string, reason?: string | null, note?: string | null) => {
    const words = [reason, note].filter(Boolean).join(" · ");
    if (words) bits.push(`${label}: ${words}`);
  };
  add("In", s.in_reason, s.in_note);
  add("Out", s.out_reason, s.out_note);
  if (s.note) bits.push(s.note);
  return bits.length ? bits.join("  |  ") : null;
}

/**
 * A calendar date, written the way the practice would write it.
 *
 * `toISOString().slice(0,10)` is the UTC date, which on Long Island is
 * tomorrow's from 8pm. That put a shift clocked on Tuesday evening under
 * Wednesday, and the day headings under the day BEFORE - a payroll report
 * that disagrees with the calendar is worse than no report.
 */
const dateKey = (d: Date, timeZone?: string): string => {
  // Built from the parts, never formatted through a locale: `en-CA` is ISO
  // on some builds and 10/27/2026 on others.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

/** Monday of the week containing `d`, as YYYY-MM-DD. */
function weekStart(d: Date): string {
  const copy = new Date(d);
  const day = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - day);
  return dateKey(copy);
}
const addDays = (iso: string, n: number) => {
  const d = new Date(iso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/* Read back at noon UTC so the label never slides to the day before. */
const dayLabel = (key: string) =>
  new Date(`${key}T12:00:00Z`).toLocaleDateString([], {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
  });
const timeOf = (iso: string, timeZone?: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone });
/** An <input type="datetime-local"> wants local wall-clock, not an ISO Z. */
const forInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function Timesheet() {
  const toast = useToast();
  const [from, setFrom] = useState(() => weekStart(new Date()));
  const [to, setTo] = useState(() => addDays(weekStart(new Date()), 6));
  const [data, setData] = useState<{ shifts: Shift[]; totals: Total[]; everyone: boolean; timezone?: string } | null>(null);
  const [editing, setEditing] = useState<Shift | null>(null);

  const load = useCallback(() => {
    api.get<{ shifts: Shift[]; totals: Total[]; everyone: boolean; timezone?: string }>("/staff/timesheet", { from, to })
      .then(setData)
      .catch(() => setData(null));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  /**
   * One block per person, not per day.
   *
   *   "The time report can this be by staff not by day? It's much easier to
   *    look at it by staff then by day. So if Christina shows up it's a list
   *    of all her shifts and tallied at the bottom. Then the next staff
   *    member with tally's of all their shifts in the time period."
   *
   * Driven off the totals the server already worked out, so the tally under
   * somebody's shifts is the same number as the row above - one source for
   * the figure that goes on a pay run. Their shifts read forwards, the way a
   * pay period is read.
   */
  const byStaff = useMemo(() => {
    const shiftsOf = new Map<string, Shift[]>();
    for (const s of data?.shifts ?? []) {
      (shiftsOf.get(s.user_id) ?? shiftsOf.set(s.user_id, []).get(s.user_id)!).push(s);
    }
    return (data?.totals ?? []).map((total) => ({
      total,
      shifts: (shiftsOf.get(total.user_id) ?? [])
        .slice()
        .sort((a, b) => a.started_at.localeCompare(b.started_at)),
    }));
  }, [data]);

  const remove = async (s: Shift) => {
    try {
      await api.delete(`/staff/shifts/${s.id}`);
      toast.success("Shift removed");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove it");
    }
  };

  return (
    <div data-timesheet className="space-y-4">
      {/* The pickers are how she chooses the payroll dates; on paper the line
          under them says which dates it was, so they only get in the way. */}
      <div className="flex flex-wrap items-end gap-2 print:hidden">
        <div>
          <Label>From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" />
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { const w = weekStart(new Date()); setFrom(w); setTo(addDays(w, 6)); }}
        >
          This week
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { const w = addDays(weekStart(new Date()), -7); setFrom(w); setTo(addDays(w, 6)); }}
        >
          Last week
        </Button>
        {/* "when I print out the report I get the total hours days shifts and
            any notes" — the page prints as it stands, so what is on screen
            between those two dates is exactly what comes out of the printer. */}
        <Button variant="secondary" size="sm" leftIcon={<Printer className="h-3.5 w-3.5" />}
          onClick={() => window.print()}>
          Print
        </Button>
      </div>

      {/* Only on paper: which dates this covers, so a printed sheet is not
          an anonymous list of hours. */}
      <p className="hidden text-sm font-medium text-zinc-900 print:block">
        Hours from {from} to {to}
      </p>

      {!data ? <Skeleton className="h-28" /> : (
        <>
          {data.totals.length === 0 ? (
            <p className="rounded-lg border border-zinc-800/70 bg-zinc-900/30 px-3 py-2 text-sm text-zinc-500">
              Nobody clocked in between those dates.
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-zinc-800">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/60 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">{data.everyone ? "Who" : "You"}</th>
                    <th className="px-3 py-2 font-medium">Days</th>
                    <th className="px-3 py-2 font-medium">Shifts</th>
                    <th className="px-3 py-2 font-medium">Notes</th>
                    <th className="px-3 py-2 text-right font-medium">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/70">
                  {data.totals.map((t) => (
                    <tr key={t.user_id}>
                      <td className="px-3 py-2 text-zinc-100">{t.staff_name ?? "Someone"}</td>
                      <td className="px-3 py-2 tabular-nums text-zinc-400">{t.days}</td>
                      <td className="px-3 py-2 text-zinc-400">
                        {t.shifts}
                        {t.open > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                            <AlertTriangle className="h-3 w-3" /> {t.open} still running
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-zinc-400">{t.notes || "-"}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-zinc-100">
                        {hoursLabel(t.hours)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Each person whole, so a page break never splits somebody's
              shifts away from their tally. */}
          {byStaff.map(({ total, shifts }) => (
            <div
              key={total.user_id}
              className="overflow-hidden rounded-xl border border-zinc-800 print:break-inside-avoid"
            >
              <p className="border-b border-zinc-800/70 bg-zinc-900/40 px-3 py-2 text-sm font-semibold text-zinc-100">
                {total.staff_name ?? "Someone"}
                {shifts[0]?.credential && (
                  <span className="ml-1 font-normal text-zinc-500">{shifts[0].credential}</span>
                )}
              </p>
              <ul className="divide-y divide-zinc-800/70">
                {shifts.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-zinc-200">
                      {dayLabel(dateKey(new Date(s.started_at), data.timezone))}
                    </span>
                    <span className="tabular-nums text-zinc-400">
                      {timeOf(s.started_at, data.timezone)} – {s.ended_at ? timeOf(s.ended_at, data.timezone) : "still on"}
                    </span>
                    <span className="w-16 text-right font-medium tabular-nums text-zinc-100">
                      {hoursLabel(s.hours)}
                    </span>
                    {s.edited && (
                      <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400" title="Corrected after the fact">
                        edited
                      </span>
                    )}
                    {data.everyone && (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditing(s)}
                          className="rounded p-1 text-zinc-500 hover:text-zinc-200 print:hidden"
                          aria-label={`Correct ${s.staff_name ?? "this shift"}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void remove(s)}
                          className="rounded p-1 text-zinc-600 hover:text-rose-400 print:hidden"
                          aria-label="Remove shift"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    {noteLine(s) && (
                      <span className="w-full text-[11px] text-amber-300/90">{noteLine(s)}</span>
                    )}
                  </li>
                ))}
              </ul>
              {/* Her tally, under her own shifts. */}
              <div className="flex items-center justify-between border-t border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm">
                <span className="text-zinc-400">
                  {total.days} {total.days === 1 ? "day" : "days"} · {total.shifts} {total.shifts === 1 ? "shift" : "shifts"}
                  {total.notes > 0 && ` · ${total.notes} with a note`}
                  {total.open > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300">
                      <AlertTriangle className="h-3 w-3" /> {total.open} still running
                    </span>
                  )}
                </span>
                <span className="font-semibold tabular-nums text-zinc-100">{hoursLabel(total.hours)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {editing && (
        <EditShift shift={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

/**
 * Correcting a shift. People forget to clock out; payroll still has to be
 * right. What it said before is kept on the row, so a correction is visible
 * rather than silent.
 */
function EditShift({ shift, onClose, onSaved }: { shift: Shift; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [startedAt, setStartedAt] = useState(forInput(shift.started_at));
  const [endedAt, setEndedAt] = useState(shift.ended_at ? forInput(shift.ended_at) : "");
  const [note, setNote] = useState(shift.note ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/staff/shifts/${shift.id}`, {
        started_at: new Date(startedAt).toISOString(),
        ended_at: endedAt ? new Date(endedAt).toISOString() : null,
        note: note.trim() || null,
      });
      toast.success("Timesheet corrected");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save that");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Correct ${shift.staff_name ?? "this shift"}`} size="sm">
      <div className="space-y-3">
        <div>
          <Label>Started</Label>
          <Input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
        </div>
        <div>
          <Label>Ended</Label>
          <Input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} />
          <p className="mt-1 text-[11px] text-zinc-500">Leave empty if they are still on.</p>
        </div>
        <div>
          {/* "change if needed just ask why the change" — required, because
              this is what payroll is paid from. */}
          <Label required>Why</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. forgot to clock out" />
        </div>
        <p className="text-[11px] text-zinc-500">
          The original times are kept on the record, with your name against the change.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" loading={saving} disabled={!note.trim()} onClick={() => void save()}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
